/**
 * <ExpiryBand> — 13-ui-screens.md §10.3, 14-theme.md §7.7.
 *
 * A full-bleed coloured band header over a HEADERLESS table.
 * Urgency reads from the band header, NEVER from the row background —
 * do not add a red row tint (FR-2.4, component-sheet caption).
 */
import type { CSSProperties, ReactNode } from 'react';

export type BandTone = 'expired' | 'due30' | 'due60' | 'due90' | 'missing';

const BAND: Record<BandTone, { bg: string; fg: string; dot: string }> = {
  expired: { bg: 'var(--status-danger)', fg: 'var(--white)', dot: 'var(--white)' },
  due30: { bg: 'var(--cm-amber-bg)', fg: 'var(--cm-amber-text)', dot: 'var(--status-warning)' },
  due60: { bg: 'var(--neutral-100)', fg: 'var(--text-body)', dot: 'var(--neutral-400)' },
  due90: { bg: 'var(--neutral-100)', fg: 'var(--text-body)', dot: 'var(--neutral-400)' },
  missing: { bg: 'var(--neutral-100)', fg: 'var(--text-body)', dot: 'var(--neutral-400)' },
};

export interface ExpiryBandProps {
  /** `EXPIRED`, `≤ 30 DAYS`, `NO DOCUMENT ON FILE`… */
  label: string;
  /** `7 workers` — already formatted, mono. */
  count: string;
  /** `Work must stop today`, `File renewal with agent`… */
  action: string;
  tone: BandTone;
  /** The headerless `<DenseTable>` rows, or the empty-band copy. */
  children: ReactNode;
}

const LABEL: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: '.14em',
  textTransform: 'uppercase',
};

export function ExpiryBand({ label, count, action, tone, children }: ExpiryBandProps) {
  const c = BAND[tone];
  return (
    <section
      style={{
        marginBottom: 16,
        background: 'var(--surface-card)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 4,
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 12px',
          background: c.bg,
          color: c.fg,
        }}
      >
        <span
          style={{ width: 8, height: 8, borderRadius: '50%', display: 'inline-block', background: c.dot }}
        />
        <span style={LABEL}>{label}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, opacity: 0.85 }}>{count}</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, opacity: 0.9 }}>{action}</span>
      </header>
      {children}
    </section>
  );
}

/** `Nothing in this band.` / for EXPIRED: `Nothing expired. Good.` */
export function EmptyBand({ expired = false }: { expired?: boolean }) {
  return (
    <div style={{ padding: '14px 12px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
      {expired ? 'Nothing expired. Good.' : 'Nothing in this band.'}
    </div>
  );
}
